import { FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BlockWrapper } from "../../../../simples/BlockWrapper";
import { Button } from "../../../../simples/Button";
import { Input } from "../../../../simples/Input";
import { TypeInput } from "../../../../simples/Input/Input";
import { Switch } from "../../../../simples/Switch";
import {
  NotificationsService,
  NotificationSettingsType,
} from "../../../../../services/Notifications";
import styles from "./NotificationSettings.module.scss";

const defaultSettings: NotificationSettingsType = {
  email: false,
  low_balance: false,
  min_balance: 0,
};

const NotificationSettings: FC = () => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(defaultSettings.email);
  const [lowBalance, setLowBalance] = useState(defaultSettings.low_balance);
  const [enabled, setEnabled] = useState(defaultSettings.email || defaultSettings.low_balance);
  const [minBalance, setMinBalance] = useState(
    String(defaultSettings.min_balance)
  );
  const [saving, setSaving] = useState(false);

  const applySettings = useCallback((data: NotificationSettingsType) => {
    setEmail(!!data.email);
    setLowBalance(!!data.low_balance);
    setMinBalance(String(data.min_balance ?? 0));
    setEnabled(!!data.email || !!data.low_balance);
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await NotificationsService.getSettings();
      applySettings(data);
    } catch (error) {
      console.error("NotificationsService.getSettings", error);
      toast.error(error instanceof Error ? error.message : "Не удалось загрузить настройки оповещений", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
      });
    } finally {
        setLoading(false);
    }
  }, [applySettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleEnabledChange = (value: boolean) => {
    setEnabled(value);
    if (!value) {
      setEmail(false);
      setLowBalance(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await NotificationsService.updateSettings({
        email: enabled ? email : false,
        low_balance: enabled ? lowBalance : false,
        min_balance: Number(minBalance) || 0,
      });

      if (response.success) {
        if (response.data) {
          applySettings(response.data);
        }
        toast.success("Настройки оповещений сохранены", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
        });
      } else {
        toast.error("Не удалось сохранить настройки", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: true,
        });
      }
    } catch (error) {
      console.error("NotificationsService.updateSettings", error);
      toast.error("Не удалось сохранить настройки", {
        position: "top-center",
        autoClose: 2000,
        hideProgressBar: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BlockWrapper isFull>
      <h3 className={styles.title}>Настройки оповещений</h3>
      <div className={styles.list}>
        <Switch
          label="Включить оповещения"
          checked={enabled}
          onChange={handleEnabledChange}
        />
        {enabled && (
          <>
            <div className={styles.section}>
              <h4 className={styles.subtitle}>Каналы оповещений</h4>
              <Switch label="Email" checked={email} onChange={setEmail} />
            </div>
            <div className={styles.section}>
              <h4 className={styles.subtitle}>Типы оповещений</h4>
              <div className={styles.sectionList}>
                <Switch
                  label="Оповещения о низком балансе"
                  checked={lowBalance}
                  onChange={setLowBalance}
                />
                {lowBalance && (
                  <div className={styles.threshold}>
                    <Input
                        typeProps={TypeInput.NUMBER}
                        label="Минимальный порог баланса"
                        value={minBalance}
                        placeholder="0"
                        onChange={setMinBalance}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <Button onClick={save} disabled={loading || saving} className={styles.button}>
        Сохранить
      </Button>
    </BlockWrapper>
  );
};

export default NotificationSettings;
